import React, { useState, useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { fetchProjectCompetitors } from "../../redux/actions/ProjectCompetitorsActions";
import { displayDateFormart, isValidArr } from "../../utility/utils";
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import ProjectCompetitorsForm from "./ProjectCompetitorsForm";
import Loader from "../../components/loader/Loader";
import { ProjectCompetitorsModel } from "../../models/ProjectCompetitorsModel";

export type Mode = "add" | "edit";

type iData = {
  id: number;
  name: string;
  createdDate: string;
};

const ProjectCompetitorsPage = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<ProjectCompetitorsModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);

  const allData = useAppSelector(
    (state) => state.projectCompetitors.all_ProjectCompetitors
  );

  const isProjectCompetitorsFetching = useAppSelector(
    (state) => state.projectCompetitors.isProjectCompetitorsFetching
  );

  const columns = useMemo(() => {
    return [
      {
        id: "id",
        numeric: false,
        disablePadding: false,
        label: "ID",
      },
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Name",
      },
      {
        id: "createdDate",
        numeric: false,
        disablePadding: false,
        label: "Created Date",
        width: 150,
      },
    ];
  }, []);

  //To fetch all Project Competitors
  useEffect(() => {
    dispatch(fetchProjectCompetitors());
  }, [dispatch]);

  useEffect(() => {
    setRootData(() =>
      allData.map((rec) => ({
        id: rec.id,
        name: rec.name,
        createdDate: displayDateFormart(rec.createdDate),
      }))
    );
  }, [allData]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const editRec = allData.find((rec) => rec.id === id);
    setMode("edit");
    setEditData(editRec);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {!isValidArr(allData) && isProjectCompetitorsFetching ? <Loader /> : null}
      <PageTitle
        title="Project Competitors"
        btnText="Add Project Competitor"
        onClick={createHandler}
      />
      <PageSearch
        placeholder="Search Project Competitors"
        searchHandler={searchHandler}
      />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={
            mode === "add"
              ? "Create Project Competitor"
              : "Edit Project Competitors"
          }
          handleModalClose={handleModalClose}
        >
          <ProjectCompetitorsForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default ProjectCompetitorsPage;
